/**
 * agentOrbit — bridge wave-2 item 4: the roster→slot mapping for
 * AgentConstellation. Pure module, no React.
 *
 * PRIOR-ART ATTESTATION: none_exists (new file). STAGE_AGENTS is NOT copied
 * here — it stays in its single home, frontend/src/components/fleet/
 * AssemblyLine.tsx (already an exported const), and is passed INTO
 * buildConstellation by the consumer. One source, zero duplication.
 *
 * ROSTER PROVENANCE (every name verified against the real session writers —
 * orchestrator-v2.0/index.js agent-class constructors, where Agent.toJSON()
 * lands `agent_type: this.type` into db.sessions at index.js:943):
 *   Stage-bound (12) — from STAGE_AGENTS (AssemblyLine.tsx:49), which mirrors
 *     the pipeline roles: gap_author/plan_critic (PLAN), executor (BUILD),
 *     schema_validator/code_reviewer/reality_verifier/impact_detector (GATE),
 *     merge_agent (MERGE), acceptance_probe/verifier (VERIFY),
 *     warroom_verifier/regression_review (DEEP).
 *   Belt (11) — the run-level agents, all real `this.type`/`super(type)`
 *     values: fixer (2447), second_opinion (2519), permission_verifier (3340),
 *     gate_health_verifier (this.type override at 3203 — the graph query
 *     "No node matching gate_health_verifier" missed it because
 *     GateHealthVerifierAgent reuses the permission_verifier MODEL slot via
 *     super() and then sets the SESSION type), rollback_agent (2640),
 *     dependency_tracker (2758), escalation_reporter (2696), doc_agent (3755),
 *     checklist_agent (3794), senior_reviewer (3567), eod_reporter (3656).
 *   KNOWN UPSTREAM MISMATCH (documented, NOT fixed here — fixing the map is
 *     AssemblyLine's call, not a fork of its source): STAGE_AGENTS DEEP lists
 *     'regression_review' but RegressionReviewAgent writes
 *     agent_type='regression_reviewer' (index.js:2369). That node (and
 *     AssemblyLine's own dot) stays dim until the map is corrected upstream.
 *
 * GEOMETRY: stage-bound agents sit at r=122, evenly spaced WITHIN their
 * stage's arc segment — the same segment math BridgeCore's arcs use
 * (arcPath: slot = 360/6, segment = [i*slot+5, (i+1)*slot-5]). Belt agents
 * sit at r=150 with a GAP_DEG break between functional groups. All positions
 * FIXED — nodes never rotate (stable click targets; rotation belongs to
 * rings that carry no information, plan §2 layer 4).
 */
import { REACTOR_STAGES } from '../fleet/ReactorCore';
import type { FleetSession } from '../../services/fleetHudApi';

/**
 * SESSION-STATE DERIVATION — the SINGLE selector + state mapping shared by
 * AgentConstellation (core nodes) and AgentRosterList (rail rows). Both
 * surfaces must show identical live state (bridge item 5, FM5), so the
 * derivation lives here exactly once; never re-implement it in a component.
 */
export type AgentSessionState = 'open' | 'completed' | 'none';

/** Sessions belonging to the active run — a session with no run_id is run-
    agnostic and always counts; an unknown activeRunId never excludes. */
export function filterRunSessions(
  sessions: FleetSession[] | undefined,
  activeRunId: string | null | undefined
): FleetSession[] | undefined {
  if (sessions === undefined) return undefined;
  return sessions.filter((s) => !s.run_id || !activeRunId || s.run_id === activeRunId);
}

function isOpenSession(s: FleetSession): boolean {
  return s.completed_at === null || s.completed_at === undefined;
}

/** AssemblyLine dotState semantics: open > completed > none. undefined
    sessions (unavailable / fetch failed) degrade to 'none' — never a lie. */
export function agentSessionState(
  runSessions: FleetSession[] | undefined,
  agentType: string
): AgentSessionState {
  if (!runSessions) return 'none';
  const ofType = runSessions.filter((s) => s.agent_type === agentType);
  if (ofType.length === 0) return 'none';
  return ofType.some(isOpenSession) ? 'open' : 'completed';
}

export interface OrbitSlot {
  agentType: string;
  orbitRadius: 122 | 150;
  shape: 'circle' | 'diamond';
  stageKey?: string;
  slotIndex: number;
  totalInSegment: number;
  /** Fixed polar angle (BridgeCore/ReactorCore polar() convention: 0° = top,
      clockwise), computed once at build time. */
  angleDeg: number;
}

/** Inner orbit (stage-bound) / outer orbit (run-level belt). */
export const STAGE_ORBIT_RADIUS = 122 as const;
export const BELT_ORBIT_RADIUS = 150 as const;

/** Angular break between belt groups — the "gap breaks" of plan §2 layer 4. */
const GAP_DEG = 14;

/**
 * Belt roster in functional groups (display order = clockwise from the top).
 * EVERY name above verified against orchestrator-v2.0/index.js — do not add
 * a name that no agent class writes as its session agent_type.
 */
export const BELT_GROUPS: readonly (readonly string[])[] = [
  ['fixer', 'second_opinion'], // repair loop
  ['permission_verifier', 'gate_health_verifier'], // run-level gates
  ['rollback_agent', 'dependency_tracker'], // recovery + cross-item tracking
  // senior_reviewer moved to STAGE_AGENTS.DEEP (2026-08-14) — it is the deep
  // verdict stage on every run, not belt support; one home only.
  ['escalation_reporter', 'doc_agent', 'checklist_agent', 'eod_reporter'], // reporting
];

/** Flattened belt roster — the canonical belt agent list. */
export const BELT_AGENTS: readonly string[] = BELT_GROUPS.flat();

/** Group id per belt agent, derived from BELT_GROUPS (unknown names fall
    into a trailing group of their own so gaps still render sanely). */
function beltGroupOf(agentType: string): number {
  const idx = BELT_GROUPS.findIndex((g) => g.includes(agentType));
  return idx === -1 ? BELT_GROUPS.length : idx;
}

/**
 * Stage segment angles — identical slot math to ReactorCore.arcPath so the
 * constellation nodes land INSIDE the arc their stage owns (r=96 there,
 * r=122 here; same angles).
 */
function stageSegment(stageKey: string, stageCount: number): { a0: number; a1: number } {
  const idx = REACTOR_STAGES.indexOf(stageKey as (typeof REACTOR_STAGES)[number]);
  const slot = 360 / stageCount;
  const i = idx === -1 ? 0 : idx;
  return { a0: i * slot + 5, a1: (i + 1) * slot - 5 };
}

/**
 * buildConstellation — pure roster→slot mapping.
 * @param stageAgents  stage → agent_type[] map (pass AssemblyLine's
 *                     STAGE_AGENTS — the single source; never a copy).
 * @param beltAgents   flat belt roster (pass BELT_AGENTS).
 */
export function buildConstellation(
  stageAgents: Readonly<Record<string, readonly string[]>>,
  beltAgents: readonly string[]
): OrbitSlot[] {
  const slots: OrbitSlot[] = [];
  const stageCount = REACTOR_STAGES.length;

  // Stage-bound circles at r=122, evenly spaced within the stage's segment.
  for (const stageKey of REACTOR_STAGES) {
    const agents = stageAgents[stageKey] ?? [];
    const { a0, a1 } = stageSegment(stageKey, stageCount);
    agents.forEach((agentType, j) => {
      slots.push({
        agentType,
        orbitRadius: STAGE_ORBIT_RADIUS,
        shape: 'circle',
        stageKey,
        slotIndex: j,
        totalInSegment: agents.length,
        angleDeg: a0 + ((j + 0.5) * (a1 - a0)) / agents.length,
      });
    });
  }

  // Belt diamonds at r=150: equal pitch with a GAP_DEG break wherever the
  // functional group changes. Span = 360 - gaps*GAP_DEG, split evenly.
  const total = beltAgents.length;
  if (total > 0) {
    let gaps = 0;
    for (let i = 1; i < total; i++) {
      if (beltGroupOf(beltAgents[i]) !== beltGroupOf(beltAgents[i - 1])) gaps++;
    }
    const pitch = (360 - gaps * GAP_DEG) / total;
    let angle = pitch / 2;
    for (let i = 0; i < total; i++) {
      if (i > 0 && beltGroupOf(beltAgents[i]) !== beltGroupOf(beltAgents[i - 1])) {
        angle += GAP_DEG;
      }
      slots.push({
        agentType: beltAgents[i],
        orbitRadius: BELT_ORBIT_RADIUS,
        shape: 'diamond',
        slotIndex: i,
        totalInSegment: total,
        angleDeg: angle,
      });
      angle += pitch;
    }
  }

  return slots;
}
